import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Paper, Typography, Grid, TextField, Button, MenuItem, Box, IconButton, Divider, Alert
} from '@mui/material';
import { Add, Remove, ArrowBack } from '@mui/icons-material';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import BSDateInput from '../common/BSDateInput';

const billNameOptions = ['P', 'K', 'R', 'O', 'W', 'Y'];
const unitOptions = ['Pcs', 'Pair', 'Set'];
const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];

const itemNameOptions = [
  'Gold Ring',
  'Gold Chain',
  'Gold Necklace',
  'Gold Bracelet',
  'Gold Earring',
  'Gold Pendant',
  'Silver Ring',
  'Silver Chain',
  'Silver Bracelet',
  'Silver Earring',
  'Silver Pendant',
  'Platinum Ring',
  'Platinum Chain',
  'Diamond Ring',
  'Diamond Necklace'
];

const defaultItem = {
  itemName: '',
  quantity: 1,
  unit: 'Pcs',
  size: '',
  description: '',
  gsb: 0,
  itemWeight: 0,
  wastage: 0,
  total: 0,
  karat: '22K',
  rate: 0,
  netAmount: 0,
  makingCharge: 0,
  stoneAmount: 0,
  totalAmount: 0
};

const defaultCustomer = {
  name: '',
  karigarId: '',
  address: '',
  phoneNumber: ''
};

const UpdateOrderForm = () => {
  const [form, setForm] = useState({
    billName: '',
    billNumber: '',
    orderDate: '',
    deliveryDate: '',
    requiredDate: '',
    status: 'Pending',
    agentName: '',
    customer: { ...defaultCustomer },
    items: [{ ...defaultItem }]
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [itemOptions, setItemOptions] = useState([]);
  const [orderId, setOrderId] = useState(null);
  const [shouldFetchOrder, setShouldFetchOrder] = useState(false);
  const lastAttemptedBillName = useRef('');
  const lastAttemptedBillNumber = useRef('');
  const [orderExists, setOrderExists] = useState(false);
  const [orderFetched, setOrderFetched] = useState(false);

  // Add refs for billName and billNumber input values
  const billNameInput = useRef('');
  const billNumberInput = useRef('');

  // Utility to safely format date for input
  const safeDate = d => d ? new Date(d).toISOString().slice(0, 10) : '';

  const fetchItems = async () => {
    try {
      const res = await api.get('/api/items');
      setItemOptions(res.data.map(item => item.name));
    } catch (err) {
      // fallback: do nothing
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // Fetch order for update
  useEffect(() => {
    if (shouldFetchOrder && billNameInput.current && billNumberInput.current) {
      api.get(`/api/shop/orders?billName=${encodeURIComponent(billNameInput.current)}&billNumber=${encodeURIComponent(billNumberInput.current)}`)
        .then(res => {
          if (Array.isArray(res.data) && res.data.length > 0) {
            setOrderExists(true);
            const order = res.data[0];
            // Format date fields for input type="date"
            const formatDate = d => d ? new Date(d).toISOString().slice(0, 10) : '';
            // Helper to ensure no undefined/null in customer
            const safeCustomer = c => ({
              name: c?.name || '',
              karigarId: c?.karigarId || '',
              address: c?.address || '',
              phoneNumber: c?.phoneNumber || ''
            });
            // Helper to ensure no undefined/null in items
            const safeItem = i => ({
              itemName: i?.itemName || '',
              quantity: i?.quantity ?? 1,
              unit: i?.unit || 'Pcs',
              size: i?.size || '',
              description: i?.description || '',
              gsb: i?.gsb ?? 0,
              itemWeight: i?.itemWeight ?? 0,
              wastage: i?.wastage ?? 0,
              total: i?.total ?? 0,
              karat: i?.karat || '22K',
              rate: i?.rate ?? 0,
              netAmount: i?.netAmount ?? 0,
              makingCharge: i?.makingCharge ?? 0,
              stoneAmount: i?.stoneAmount ?? 0,
              totalAmount: i?.totalAmount ?? 0
            });
            setForm({
              ...order,
              orderDate: order.orderDate || '',
              deliveryDate: order.deliveryDate || '',
              requiredDate: order.requiredDate || '',
              status: order.status || 'Pending',
              agentName: order.agentName || '',
              customer: safeCustomer(order.customer || {}),
              items: Array.isArray(order.items) && order.items.length > 0 ? order.items.map(safeItem) : [{ ...defaultItem }],
              billName: order.billName || '',
              billNumber: order.billNumber || ''
            });
            setOrderId(order._id);
            setOrderFetched(true);
            setError('');
          } else {
            setOrderExists(false);
            setOrderFetched(false);
            setError('Order not found.');
          }
        })
        .catch(() => { setOrderExists(false); setOrderFetched(false); setError('Order not found.'); });
      setShouldFetchOrder(false);
    }
  }, [shouldFetchOrder]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'billName') localStorage.setItem('sharedBillName', value);
    if (name === 'billNumber') localStorage.setItem('sharedBillNumber', value);
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, customer: { ...prev.customer, [name]: value } }));
  };

  const handleItemChange = (idx, e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === idx ? { ...item, [name]: value } : item)
    }));
  };

  const validate = () => {
    if (!form.billName || !form.billNumber || !form.orderDate || !form.deliveryDate || !form.requiredDate || !form.agentName) return false;
    if (!form.customer.name || !form.customer.karigarId || !form.customer.address || !form.customer.phoneNumber) return false;
    if (!form.items.length) return false;
    for (let i = 0; i < form.items.length; i++) {
      const item = form.items[i];
      if (!item.itemName || !item.quantity || !item.unit || !item.itemWeight || !item.karat) return false;
      // Only fail if these are null, undefined, or empty string, but allow 0
      if (
        item.total === '' || item.total === null || item.total === undefined ||
        item.netAmount === '' || item.netAmount === null || item.netAmount === undefined ||
        item.totalAmount === '' || item.totalAmount === null || item.totalAmount === undefined
      ) return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validate()) {
      setError('Please fill all required fields.');
      return;
    }
    if (!orderExists || !orderId) {
      setError('No order loaded to update.');
      return;
    }
    setLoading(true);
    try {
      // Calculate totalAmount for each item and for the order
      const computedItems = form.items.map(item => {
        const weight = parseFloat(item.itemWeight) || 0;
        const wastage = parseFloat(item.wastage) || 0;
        const rate = parseFloat(item.rate) || 0;
        const makingCharge = parseFloat(item.makingCharge) || 0;
        const stoneAmount = parseFloat(item.stoneAmount) || 0;
        const total = weight + wastage;
        const netAmount = weight ? ((weight / 11.664) * rate) : 0;
        const totalAmount = netAmount + makingCharge + stoneAmount;
        return {
          ...item,
          total: total ? total.toFixed(3) : '',
          netAmount: netAmount ? netAmount.toFixed(2) : '',
          totalAmount: totalAmount ? totalAmount.toFixed(2) : ''
        };
      });
      const orderTotalAmount = computedItems.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
      const payload = {
        ...form,
        orderDate: form.orderDate,
        deliveryDate: form.deliveryDate,
        requiredDate: form.requiredDate,
        items: computedItems.map((item, idx) => ({
          ...item,
          _id: form.items[idx]._id || undefined, // include _id if present
          quantity: Number(item.quantity),
          gsb: Number(item.gsb),
          itemWeight: Number(item.itemWeight),
          wastage: Number(item.wastage),
          total: Number(item.total),
          rate: Number(item.rate),
          netAmount: Number(item.netAmount),
          makingCharge: Number(item.makingCharge),
          stoneAmount: Number(item.stoneAmount),
          totalAmount: Number(item.totalAmount)
        })),
        totalAmount: orderTotalAmount
      };
      await api.put(`/api/shop/orders/${orderId}`, payload);
      setSuccess('Order updated successfully!');
      setForm({
        billName: '',
        billNumber: '',
        orderDate: '',
        deliveryDate: '',
        requiredDate: '',
        status: 'Pending',
        agentName: '',
        customer: { ...defaultCustomer },
        items: [{ ...defaultItem }]
      });
      localStorage.removeItem('sharedBillName');
      localStorage.removeItem('sharedBillNumber');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update order');
    }
    setLoading(false);
  };

  // Add computedItems logic as in OrderForm.js
  const computedItems = form.items.map(item => {
    const weight = parseFloat(item.itemWeight) || 0;
    const wastage = parseFloat(item.wastage) || 0;
    const rate = parseFloat(item.rate) || 0;
    const makingCharge = parseFloat(item.makingCharge) || 0;
    const stoneAmount = parseFloat(item.stoneAmount) || 0;
    const total = weight + wastage;
    const netAmount = weight ? ((weight / 11.664) * rate) : 0;
    const totalAmount = netAmount + makingCharge + stoneAmount;
    return {
      ...item,
      total: total ? total.toFixed(3) : '',
      netAmount: netAmount ? netAmount.toFixed(2) : '',
      totalAmount: totalAmount ? totalAmount.toFixed(2) : ''
    };
  });

  // Update refs as user types
  const handleBillNameChange = (e) => {
    billNameInput.current = e.target.value;
    setForm(prev => ({ ...prev, billName: e.target.value }));
    localStorage.setItem('sharedBillName', e.target.value);
  };
  const handleBillNumberChange = (e) => {
    billNumberInput.current = e.target.value;
    setForm(prev => ({ ...prev, billNumber: e.target.value }));
    localStorage.setItem('sharedBillNumber', e.target.value);
  };

  return (
    <Container maxWidth={false} sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" color="primary">Back</Button>
        </Box>
        <Typography variant="h4" gutterBottom color="primary">
          Update Order
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {!orderFetched && (
          <Box component="form" sx={{ mb: 4 }} onSubmit={e => { e.preventDefault(); setShouldFetchOrder(true); }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={2}>
                <TextField select label="Bill Name" name="billName" value={form.billName} onChange={handleBillNameChange} required fullWidth size="small">
                  {billNameOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField label="Bill Number" name="billNumber" value={form.billNumber} onChange={handleBillNumberChange} required fullWidth autoComplete="off" size="small" />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button type="submit" variant="contained" color="primary">Fetch Order</Button>
              </Grid>
            </Grid>
          </Box>
        )}
        {orderFetched && (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={2}>
                <TextField select label="Bill Name" name="billName" value={form.billName} onChange={handleChange} onBlur={e => {
                  billNameInput.current = e.target.value;
                  // Only trigger fetch if both fields are filled
                  if (billNameInput.current && billNumberInput.current) setShouldFetchOrder(true);
                }} required fullWidth size="small">
                  {billNameOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField label="Bill Number" name="billNumber" value={form.billNumber} onChange={handleChange} onBlur={e => {
                  billNumberInput.current = e.target.value;
                  if (billNameInput.current && billNumberInput.current) setShouldFetchOrder(true);
                }} required fullWidth autoComplete="off" size="small" />
              </Grid>
              <Grid item xs={12} sm={2}>
                <BSDateInput
                  label="Order Date"
                  value={form.orderDate}
                  onChange={(date) => setForm({ ...form, orderDate: date })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <BSDateInput
                  label="Delivery Date"
                  value={form.deliveryDate}
                  onChange={(date) => setForm({ ...form, deliveryDate: date })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <BSDateInput
                  label="Required Date"
                  value={form.requiredDate}
                  onChange={(date) => setForm({ ...form, requiredDate: date })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField select label="Status" name="status" value={form.status} onChange={handleChange} required fullWidth size="small">
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField label="Agent Name (Salesman)" name="agentName" value={form.agentName} onChange={handleChange} required fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField label="Customer Name" name="name" value={form.customer.name} onChange={handleCustomerChange} required fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField label="Karigar ID" name="karigarId" value={form.customer.karigarId} onChange={handleCustomerChange} required fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField label="Address" name="address" value={form.customer.address} onChange={handleCustomerChange} required fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField label="Phone Number" name="phoneNumber" value={form.customer.phoneNumber} onChange={handleCustomerChange} required fullWidth size="small" />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Order Items</Typography>
              </Grid>
              {computedItems.map((item, idx) => (
                <React.Fragment key={idx}>
                  <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        select
                        label="Item Name"
                        name="itemName"
                        value={item.itemName}
                        onChange={e => handleItemChange(idx, e)}
                        required
                        fullWidth
                        size="small"
                      >
                        {itemOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField type="number" label="Qty" name="quantity" value={item.quantity} onChange={e => handleItemChange(idx, e)} required fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField select label="Unit" name="unit" value={item.unit} onChange={e => handleItemChange(idx, e)} required fullWidth size="small">
                        {unitOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField label="Size" name="size" value={item.size} onChange={e => handleItemChange(idx, e)} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField label="Description" name="description" value={item.description} onChange={e => handleItemChange(idx, e)} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField label="GSB" name="gsb" value={item.gsb} onChange={e => handleItemChange(idx, e)} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField type="number" label="Weight" name="itemWeight" value={item.itemWeight} onChange={e => handleItemChange(idx, e)} required fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField type="number" label="Wastage" name="wastage" value={item.wastage} onChange={e => handleItemChange(idx, e)} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField label="Total" name="total" value={item.total} fullWidth InputProps={{ readOnly: true }} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField select label="Karat" name="karat" value={item.karat} onChange={e => handleItemChange(idx, e)} required fullWidth size="small">
                        {karatOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField type="number" label="Rate" name="rate" value={item.rate} onChange={e => handleItemChange(idx, e)} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField label="Net Amt" name="netAmount" value={item.netAmount} fullWidth InputProps={{ readOnly: true }} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField type="number" label="Making Chg" name="makingCharge" value={item.makingCharge} onChange={e => handleItemChange(idx, e)} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField type="number" label="Stone Amt" name="stoneAmount" value={item.stoneAmount} onChange={e => handleItemChange(idx, e)} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <TextField label="Total Amt" name="totalAmount" value={item.totalAmount} fullWidth InputProps={{ readOnly: true }} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton onClick={() => {
                        setForm(prev => ({
                          ...prev,
                          items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== idx) : prev.items
                        }));
                      }} disabled={computedItems.length === 1}><Remove /></IconButton>
                      <IconButton onClick={() => {
                        setForm(prev => ({
                          ...prev,
                          items: [...prev.items, { ...defaultItem }]
                        }));
                      }}><Add /></IconButton>
                    </Grid>
                  </Grid>
                </React.Fragment>
              ))}
              <Divider sx={{ my: 3 }} />
              <Button type="submit" variant="contained" color="primary" disabled={loading}>
                {loading ? 'Updating...' : 'Update Order'}
              </Button>
            </Grid>
          </form>
        )}
      </Paper>
    </Container>
  );
};

export default UpdateOrderForm; 